import { useEffect, useState } from "react";
import type {
  RingClientApi,
  SceneLightInit,
  SceneLightPatch,
  SceneLightShadowParams,
  SceneLightState,
  SceneState,
} from "../../../../shared/api-contract";
import type { BindingCtx, DomainRead } from "../../../../state/editor/types";
import { useDomainState, useMutate } from "../../../../state/editor/useEditor";
import { Icon } from "../../../kit";
import {
  CompactCheck,
  CompactColor,
  CompactNumber,
  CompactSelect,
  CompactSlider,
  MiniBtn,
  Row,
} from "../../inspector/rows";

const MAP_SIZES = ["512", "1024", "2048", "4096"];

const DEFAULT_SHADOW: SceneLightShadowParams = {
  mapSize: 1024,
  radius: 20,
  blurSamples: 32,
  bias: -0.00005,
  normalBias: 0.015,
  opacity: 1,
};

/**
 * The dynamic multi-light system as a single custom inspector row. Ported from
 * the old LightsMenu LightCard + add/update/remove UI, but it talks to the
 * editor store instead of lifted Toolbar state: it reads `scene.sceneLights`
 * and every mutation goes through `useMutate("scene", …)` with an optimistic
 * patch + the RPC, reconciling the returned `SceneLightState[]` back into
 * `scene.sceneLights` (every scene-light mutator returns the fresh list, the
 * band `apply()` pattern).
 */
export function LightsControl(_props: { ctx: BindingCtx; read: DomainRead }) {
  const scene = useDomainState("scene");
  const mutate = useMutate();
  const lights = scene.sceneLights ?? [];

  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Re-sync from the viewer on mount (catches lights added outside the editor,
  // e.g. a settings-JSON import). listSceneLights returns the current list.
  useEffect(() => {
    mutate("scene", {
      // Same reason as `run` below: a failed re-sync would otherwise leave the
      // panel silently claiming the scene has no extra lights.
      call: (api) =>
        Promise.resolve(api.listSceneLights()).catch((e: unknown) => {
          setError(e instanceof Error ? e.message : String(e));
          throw e;
        }),
      reconcile: (res, prev) => ({ ...(prev as SceneState), sceneLights: res as SceneLightState[] }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Fire a list-returning mutator with an optimistic light list. */
  const run = (
    optimistic: SceneLightState[],
    call: (api: RingClientApi) => SceneLightState[] | Promise<SceneLightState[]>,
  ) => {
    setError(null);
    mutate("scene", {
      patch: { sceneLights: optimistic },
      // The store's only failure handling is `onError` → console.warn plus a
      // silent refresh("scene") that snaps the optimistic patch back. So a
      // rejected light mutation — most often SceneLights._assertShadowBudget
      // ("Shadow caster limit reached…", 4 desktop / 2 mobile) after ticking
      // "Cast Shadow" — looked to the user like the checkbox simply refused to
      // stay on, with no reason given. Surface the message here, then rethrow so
      // the store still pulls the truth back.
      call: (api) =>
        Promise.resolve(call(api)).catch((e: unknown) => {
          setError(e instanceof Error ? e.message : String(e));
          throw e;
        }),
      reconcile: (res, prev) => ({ ...(prev as SceneState), sceneLights: res as SceneLightState[] }),
    });
  };

  const addLight = (init: SceneLightInit) => run(lights, (api) => api.addSceneLight(init));

  const patchLight = (id: string, patch: SceneLightPatch) => {
    const optimistic = lights.map((l) =>
      l.id === id
        ? {
            ...l,
            ...patch,
            shadow: patch.shadow
              ? ({ ...(l.shadow ?? {}), ...patch.shadow } as SceneLightShadowParams)
              : l.shadow,
          }
        : l,
    );
    run(optimistic, (api) => api.updateSceneLight(id, patch));
  };

  const removeLight = (id: string) =>
    run(lights.filter((l) => l.id !== id), (api) => api.removeSceneLight(id));

  const clearLights = () => run([], (api) => api.clearSceneLights());

  return (
    <div className="flex flex-col gap-2 px-3 py-2">
      <div className="flex flex-wrap gap-1.5">
        <MiniBtn icon="add" onClick={() => addLight({ type: "directional" })}>Directional</MiniBtn>
        <MiniBtn icon="add" onClick={() => addLight({ type: "point" })}>Point</MiniBtn>
        <MiniBtn icon="add" onClick={() => addLight({ type: "spot" })}>Spot</MiniBtn>
        <MiniBtn icon="add" onClick={() => addLight({ type: "area" })}>Softbox</MiniBtn>
      </div>

      {error ? <p className="px-0.5 text-[12px] leading-snug text-ed-red">{error}</p> : null}

      {lights.length === 0 ? (
        <p className="px-0.5 text-[12px] text-ed-text-3">
          No extra lights. Add one above or pick a studio preset.
        </p>
      ) : null}

      {lights.map((l) => (
        <LightCard
          key={l.id}
          light={l}
          open={openId === l.id}
          onToggleOpen={() => setOpenId(openId === l.id ? null : l.id)}
          onPatch={(patch) => patchLight(l.id, patch)}
          onRemove={() => removeLight(l.id)}
        />
      ))}

      {lights.length > 0 ? (
        <MiniBtn icon="delete" onClick={clearLights}>Remove All Lights</MiniBtn>
      ) : null}
    </div>
  );
}

function LightCard({
  light: l,
  open,
  onToggleOpen,
  onPatch,
  onRemove,
}: {
  light: SceneLightState;
  open: boolean;
  onToggleOpen: () => void;
  onPatch: (patch: SceneLightPatch) => void;
  onRemove: () => void;
}) {
  const shadow = l.shadow ?? DEFAULT_SHADOW;
  return (
    <div className="rounded-lg border border-ed-border bg-white">
      <div className="flex items-center gap-1.5 px-2 py-1.5">
        <button onClick={onToggleOpen} className="flex min-w-0 flex-1 items-center gap-1.5 text-left">
          <Icon name={open ? "expand_more" : "chevron_right"} size={14} />
          <span className="truncate text-[13px] text-ed-text">{l.name}</span>
          <span className="shrink-0 rounded bg-ed-panel-2 px-1.5 py-0.5 text-[12px] font-medium text-ed-text-3">
            {l.type}
          </span>
        </button>
        <button
          title="Show helper gizmo"
          onClick={() => onPatch({ helperVisible: !l.helperVisible })}
          className={l.helperVisible ? "text-ed-accent" : "text-ed-text-3"}
        >
          <Icon name={l.helperVisible ? "visibility" : "visibility_off"} size={14} />
        </button>
        <button title="Remove light" onClick={onRemove} className="text-ed-text-3 hover:text-error">
          <Icon name="delete" size={14} />
        </button>
      </div>

      {open ? (
        <div className="flex flex-col border-t border-ed-border/60 py-1">
          <Row label="Colour">
            <CompactColor value={l.color} onChange={(v) => onPatch({ color: v })} />
          </Row>
          <Row label="Intensity">
            <CompactSlider value={l.intensity} min={0} max={10} step={0.01} onChange={(v) => onPatch({ intensity: v })} />
          </Row>
          <Row label="Azimuth">
            <CompactSlider value={l.azimuthDeg} min={-180} max={180} step={1} unit="°" onChange={(v) => onPatch({ azimuthDeg: v })} />
          </Row>
          <Row label="Elevation">
            <CompactSlider value={l.elevationDeg} min={-90} max={90} step={1} unit="°" onChange={(v) => onPatch({ elevationDeg: v })} />
          </Row>
          <Row label="Distance">
            <CompactSlider value={l.distanceFactor} min={0.5} max={8} step={0.1} unit="×" onChange={(v) => onPatch({ distanceFactor: v })} />
          </Row>

          {l.type === "spot" ? (
            <>
              <Row label="Cone Angle">
                <CompactSlider value={l.angleDeg ?? 30} min={5} max={60} step={1} unit="°" onChange={(v) => onPatch({ angleDeg: v })} />
              </Row>
              <Row label="Penumbra">
                <CompactSlider value={l.penumbra ?? 0.5} min={0} max={1} step={0.01} onChange={(v) => onPatch({ penumbra: v })} />
              </Row>
            </>
          ) : null}

          {l.type === "area" ? (
            <>
              <Row label="Width">
                <CompactSlider value={l.widthFactor ?? 1.5} min={0.2} max={5} step={0.1} unit="×" onChange={(v) => onPatch({ widthFactor: v })} />
              </Row>
              <Row label="Height">
                <CompactSlider value={l.heightFactor ?? 1.5} min={0.2} max={5} step={0.1} unit="×" onChange={(v) => onPatch({ heightFactor: v })} />
              </Row>
            </>
          ) : null}

          {l.type !== "area" ? (
            <Row label="Cast Shadow">
              <CompactCheck checked={l.castShadow} onChange={(v) => onPatch({ castShadow: v })} />
            </Row>
          ) : (
            <p className="px-3 py-1 text-[11px] text-ed-text-3">
              Softbox lights cannot cast shadows (three.js limitation).
            </p>
          )}

          {l.castShadow ? (
            <>
              <Row label="Shadow Opacity">
                <CompactSlider value={shadow.opacity} min={0} max={1} step={0.01} onChange={(v) => onPatch({ shadow: { ...shadow, opacity: v } })} />
              </Row>
              <Row label="Shadow Softness">
                <CompactSlider value={shadow.radius} min={0} max={40} step={0.5} onChange={(v) => onPatch({ shadow: { ...shadow, radius: v } })} />
              </Row>
              <Row label="Blur Samples">
                <CompactSlider value={shadow.blurSamples} min={1} max={64} step={1} onChange={(v) => onPatch({ shadow: { ...shadow, blurSamples: v } })} />
              </Row>
              <Row label="Map Size">
                <CompactSelect
                  value={String(shadow.mapSize)}
                  options={MAP_SIZES.map((s) => ({ id: s, label: `${s}px` }))}
                  onChange={(v) => onPatch({ shadow: { ...shadow, mapSize: Number(v) } })}
                />
              </Row>
              <Row label="Bias">
                <CompactNumber value={shadow.bias} step={0.00001} width={80} onChange={(v) => onPatch({ shadow: { ...shadow, bias: v } })} />
              </Row>
              <Row label="Normal Bias">
                <CompactNumber value={shadow.normalBias} step={0.001} width={80} onChange={(v) => onPatch({ shadow: { ...shadow, normalBias: v } })} />
              </Row>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
